import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { OverviewDisplayComponent } from '@products/overview/overview-display-component.interface';
import { OverviewComponentConfig } from '@products/overview/overview-component.config';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { ProductDetailConfiguration, TabName } from '@types';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { Subscription } from 'rxjs';
import { Logger } from '@utils/logger';
import { getWidenDocumentUrl } from '@utils/link-utils';
import { FundDocument } from '@components/products/types/fund-document.type';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FundHeaderService } from '../services/fund-header.service';
import { TranslateService } from '@components/shared/translate/translate.service';
import { PortfolioService } from '@components/products/portfolio/portfolio.service';
import { PortfolioComponentConfig } from '@components/products/portfolio/portfolio-component.config';
import {
  PortfolioAllocationTableData,
  PortfolioTopHoldingTable,
} from '@components/products/models';
import kebabCase from 'lodash/kebabCase';
import { SiteConfigService } from '@services/site-config.service';
import { CaveatService } from '@components/products/caveats/services/caveat.service';

const logger = Logger.getLogger('MainFundContentComponent');
const suitableForIcons = {
  'Capital appreciation': 'capital_appreciation.svg',
  "Child's Marriage": 'childs_marriage_corpus.svg',
  'Childs Marriage': 'childs_marriage_corpus.svg',
  'Diversification from equities': 'diversification_from_equities.svg',
  'Long Term Wealth Creation': 'long_term_wealth_creation.svg',
  'Overseas Diversification of Assets':
    'overseas_diversification_of_assets.svg',
  'Tax Saving': 'tax_saving.svg',
  'Education corpus for overseas education': 'overseas_education_corpus.svg',
  'Regular Income': 'regular_income.svg',
  'Parking of short term funds': 'short_term_corpus.svg',
  'Retirement Corpus': 'retirement_corpus.svg',
  'Reasonable Capital appreciation': 'reasonable_capital_appreciation.svg',
  'Education Corpus': 'education_corpus.svg',
  'Emergency Corpus': 'emergency_corpus.svg',
  'Pension Plan': 'pension_plan.svg',
  'Short Term Corpus': 'short_term_corpus.svg',
};
const alternativeToIcons = {
  'Focused Investments in Companies with High Growth Potential':
    'investments_in_relatively_under_valued_companies.svg',
  'Government Bonds ': 'government_bonds.svg',
  'Investments in Asian (excluding Japan) Companies':
    'investments_in_overseas_companies.svg',
  'Investments in Dividend Paying Companies':
    'investments_in_dividend_paying_companies.svg',
  'Investments in European companies': 'investments-in-european-companies.svg',
  'Investments in Infrastructure and Infrastructure related companies':
    'investments_in_infrastructure_companies.svg',
  'Investments in Large, Mid and Small Cap Companies':
    'investments_in_multi_sized_companies.svg',
  'Investments in Nifty Index Companies': 'investments_in_index_comapanies.svg',
  'Investments in Predominantly Large and Mid-Cap Companies':
    'investments_in_large_sized_companies.svg',
  'Investments in Technology and technology related Companies':
    'investments_in_technology_companies.svg',
  'Investments in U.S. companies': 'investments_in_us_companies.svg',
  'Investments in companies that fit into a Value Investment strategy':
    'investments_fit_value-strategy.svg',
  'Investments in companies undergoing Special Situations':
    'investments_undergoing_special_situations.svg',
  'Investments in predominantly Large Cap Companies':
    'investments_in_predom_large_mid_cap_companies.svg',
  'Investments in predominantly Mid-Cap Companies':
    'investments_in_mid_sized_companies.svg',
  'Investments in predominantly Small Cap Companies':
    'investments_in_predom_mid_small_cap_companies.svg',
  'Medium Term Deposit': 'medium_term_deposit.svg',
  'Medium Term Savings Instruments': 'medium_term_deposit.svg',
  'Multi Asset Investments': 'multi_asset_investment.svg',
  'Retirement Plans': 'retirement_plan.svg',
  'Savings Account / Current Account': 'saving_or_current_account.svg',
  'Short Term Savings Instruments': 'short_term_deposit.svg',
  'Tax Saving Instrument': 'tax_saving_instruments.svg',
  'Tax Saving Instruments': 'tax_saving_instruments.svg',
};

@Component({
  selector: 'ft-main-fund-content',
  templateUrl: './main-fund-content.component.html',
  styleUrls: ['./main-fund-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MainFundContentComponent
  extends BaseProductDetailComponent
  implements OverviewDisplayComponent, OnDestroy {
  dataSubscription: Subscription[] = [];
  fundDocument: FundDocument;
  isPopulated = true;
  fundVideo: SafeResourceUrl;
  suitableFor = [];
  alternativeFor: string;
  whyInvestTitle: string;
  whatisFund:string;
  fundData: string;
  topTen: string[];
  topTenDate: string;
  linkToPortfolio: string;
  isSegregated = false;
  segregatedCaveats = '';
  bannerImage: string;
  bannerImageMobile: string;
  fundName = '';

  constructor(
    private fundHeaderService: FundHeaderService,
    private fundPortfolioService: PortfolioService,
    private translateService: TranslateService,
    private caveatService: CaveatService,
    private siteConfig: SiteConfigService,
    private sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }





  populate(
    componentConfig: OverviewComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    const documents = component.getModels()?.documents;

    this.fundDocument = page.getContent(documents)?.getData<FundDocument>();
    this.bannerImage = getWidenDocumentUrl(
      this.fundDocument?.fundbanner?.bannerImage?.widenDocument
    );
    this.bannerImageMobile = getWidenDocumentUrl(
      this.fundDocument?.fundbannermobile?.bannerImage?.widenDocument
    );
    logger.debug(this.fundDocument);
    if (this.fundDocument?.fundvideo) {
      this.fundVideo = this.sanitizer.bypassSecurityTrustResourceUrl(// NOSONAR
        `https://www.youtube.com/embed/${this.fundDocument.fundvideo}?rel=0`
      );
    }
    this.dataSubscription.push(
      this.fundHeaderService.getFundHeaderState$().subscribe((data) => {
        this.alternativeFor = data.alternativeFor;
        // console.log(data);
        this.whyInvestTitle = this.translateService
          .instant('products.overview-why-invest')
          .replace('FUND_NAME', data.fundName);
        this.whatisFund = this.translateService
        .instant('products.overview-what-is-fund')
        .replace('FUND_NAME', data.fundName);
        this.fundData = data.fundName;
        this.goToRecentSearch();
        this.isPopulated = true;
        this.changeDetector.detectChanges();
      })
    );
    this.dataSubscription.push(
      this.fundPortfolioService
        .getPortfolioTableData$(
          PortfolioComponentConfig.TOP_TEN_HOLDINGS_TABLE,
          this.brConfig.fundId
        )
        .subscribe((data) => {
          const tableData: PortfolioAllocationTableData = data.payload as PortfolioAllocationTableData;
          logger.debug(tableData);
          if (tableData) {
            const allocations: PortfolioTopHoldingTable[] =
              tableData.allocations;
            this.topTen = allocations?.map(
              (dp) => `${dp.holdingName}&nbsp;(${dp.sectorName})`
            );
            //console.log(this.topTen)
            /*this.topTenTitle = this.translateService
              .instant('products.overview-top-10')
              .replace('AS_OF_DATE', tableData.asOfDate);*/
            this.topTenDate = tableData.asOfDate;
            this.linkToPortfolio = this.translateService
              .instant('products.link-to-portfolio')
              .replace(
                'PORTFOLIO_LINK',
                this.siteConfig.getFundLink(
                  this.brConfig.fundId,
                  TabName.PORTFOLIO,
                  tableData.fundName
                )
              );
          }
          this.isPopulated = true;
          this.changeDetector.detectChanges();
        })
    );
    if (this.siteConfig.isSegregatedFund(this.brConfig.fundId)) {
      this.dataSubscription.push(
        this.caveatService
          .getLegals$('FundOverviewSegregated')
          .subscribe((segregatedCaveats) => {
            this.isSegregated = true;
            this.segregatedCaveats = segregatedCaveats.join(' ');
            this.changeDetector.detectChanges();
          })
      );
    }
  }

  goToRecentSearch() {
    // Add funds and set localStorage value
    let detStorage = JSON.parse(localStorage.getItem("recent-search"));
    let funds = { fundName: this.fundData }

    const index = detStorage && detStorage.findIndex((item) => item.fundName === funds.fundName);

    if (detStorage === null) {
      localStorage.setItem("recent-search", JSON.stringify([funds]));
    } else {
      if (detStorage.length < 9) {
        if (index != 0 && !index || index === -1) {
          detStorage.unshift(funds);
          localStorage.setItem("recent-search", JSON.stringify(detStorage));
        }
      }
      else {
        if (index != 0 && !index || index === -1) {
          detStorage.splice(8, 1);
          detStorage.unshift(funds);
          localStorage.setItem("recent-search", JSON.stringify(detStorage));
        }
      }
    }
  }

  getSuitableForIcon(suitableFor: string) {
    return this.sanitizer.bypassSecurityTrustUrl(// NOSONAR
      '/assets/images/suitable-for/' + suitableForIcons[suitableFor]
    );
  }

  getAlternativeToIcon(alternativeTo: string) {
    const icon =
      alternativeToIcons[alternativeTo] || alternativeToIcons['kj lk'];
    return this.sanitizer.bypassSecurityTrustUrl(// NOSONAR
      '/assets/images/alternative-to/' + icon
    );
  }

  scrollTo(param: any) {
    const elementToScroll = document.getElementById(param);
    if (elementToScroll) {
      // Scroll to the element with a delay of 10 milliseconds (or any desired delay)
      elementToScroll.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    }
  }

  careesFirstMenu(start: any, end: any) {
    const menuarrayvalue = [];
    if (this.topTen) {
      for (let i = start; i <= end; i++) {
        let setVal = this.topTen
        menuarrayvalue.push(setVal[i]);
      }
    }
    return menuarrayvalue;
  }

  ngOnDestroy(): void {
    this.dataSubscription.forEach((sub) => sub?.unsubscribe());
  }
}
